/**
 * LoadingModal Component
 *
 * Displays a modal overlay with a loading spinner and message.
 * Used to show feedback during long-running operations like exports.
 */

import { CSSProperties } from 'react';

interface LoadingModalProps {
  message: string;
  isVisible: boolean;
}

function LoadingModal({ message, isVisible }: LoadingModalProps) {
  if (!isVisible) return null;

  const overlayStyles: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  };

  const modalStyles: CSSProperties = {
    backgroundColor: 'var(--nb-bg-primary)',
    border: '2px solid var(--nb-accent-primary)',
    borderRadius: 'var(--nb-radius-md)',
    padding: 'var(--nb-space-lg)',
    minWidth: '300px',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  };

  const spinnerStyles: CSSProperties = {
    width: '48px',
    height: '48px',
    border: '4px solid var(--nb-border-primary)',
    borderTop: '4px solid var(--nb-accent-primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto var(--nb-space-md)',
  };

  const messageStyles: CSSProperties = {
    color: 'var(--nb-text-primary)',
    fontSize: 'var(--nb-font-size-md)',
    fontWeight: 500,
  };

  return (
    <div style={overlayStyles}>
      <div style={modalStyles}>
        <div style={spinnerStyles} />
        <div style={messageStyles}>{message}</div>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default LoadingModal;
